/**
 * 노출 시간 기본값. 서버가 자산을 만들 때와 플레이어가 재생 길이를 정할 때 **같은 값**을
 * 봐야 한다. 한쪽만 바꾸면 "보낸 대로 안 나온다" 가 되고, 그 어긋남은 화면에서만 드러난다.
 */
export const DEFAULT_DWELL_SEC = {
    /** 정지 이미지. 사람이 읽고 넘어가기에 적당한 길이. */
    image: 8,
    /** 외부 웹 페이지. 대시보드류는 조금 더 오래 둔다. */
    web: 30,
    /** 올린 HTML 파일(2026-10-19) — 웹 페이지와 같은 쓰임이라 같은 값이다. */
    html: 30,
    /**
     * 영상 — **길이를 모를 때** 끝나기를 기다리는 시간. 영상은 노출 시간과 상관없이 끝까지 재생한다
     * (`videoWaitSec`). 이 값은 담을 때 항목에 들어가는 기본값이기도 하다.
     */
    video: 300,
};
/**
 * 영상은 **끝까지 재생한 뒤** 다음으로 넘어간다(2026-10-07 사용자 결정 — 영상과 다른 콘텐츠를 한
 * 플레이리스트에 섞는다). 항목의 노출 시간은 영상에 쓰지 않는다.
 *
 * 예전에는 노출 시간이 상한이라 이미지와 섞으면 영상이 8초에 잘렸고, 그래서 영상은 단독으로만 담게 했다.
 *
 * 화면이 끝을 기다리는 데도 끝이 있어야 한다 — 디코더가 오류 없이 멈추면 `ended` 가 오지 않는다. 그
 * 여유가 `VIDEO_END_GRACE`: 길이의 10%, 적어도 10초. 저사양 기기가 프레임을 떨궈도 재생 시각은 소리
 * 시계를 따르므로 이만하면 끝이 먼저 온다.
 */
const VIDEO_END_GRACE = { ratio: 0.1, minSec: 10 };
/** 영상이 끝나기를 기다리는 최대 시간(초). 길이를 모르면 `DEFAULT_DWELL_SEC.video` 를 길이로 본다. */
export function videoWaitSec(durationSec) {
    const length = durationSec !== null && durationSec > 0 ? durationSec : DEFAULT_DWELL_SEC.video;
    return length + Math.max(VIDEO_END_GRACE.minSec, length * VIDEO_END_GRACE.ratio);
}
/**
 * 해석 결과에 실을 영상 항목의 노출 시간 — **옛 화면(에이전트 0.7.8 이하)을 위한 값이다.**
 *
 * 옛 화면은 `min(영상 길이, 노출 시간)` 까지만 틀어, 노출 시간이 영상보다 짧으면 잘랐다. 길이를 아는
 * 영상은 노출 시간을 길이(올림) 이상으로 올려 보내 옛 화면도 끝까지 틀게 한다. 새 화면은 이 값을 쓰지 않는다.
 */
export function videoDwellSec(durationSec, dwellSec) {
    if (durationSec === null || !(durationSec > 0))
        return dwellSec;
    return Math.max(dwellSec, Math.ceil(durationSec));
}
/**
 * 옛 화면이 그릴 줄 아는 종류 — 하트비트로 `content_types` 를 알리지 않은 화면은 이것만 받는다.
 *
 * **올린 HTML 은 알린 화면에만 보낸다.** 옛 화면(0.7.16 이하)은 모르는 종류를 외부 웹 페이지처럼 띄우는데, 그때의 샌드박스는
 * 같은 출처를 허락한다(`allow-same-origin`) — 남이 짠 HTML 이 플레이어와 같은 출처로 돌면 플레이어를 조작할 수 있다.
 */
export const LEGACY_CONTENT_TYPES = ['image', 'video', 'web', 'template'];
/**
 * 장면 전환 — 다음 항목으로 넘어갈 때의 효과. 어떻게 움직이는지는 `transitions.ts` 가 정한다(플레이어·콘솔 미리보기가 함께 쓴다).
 *
 * **모르는 값은 페이드로 그린다.** 새 효과를 더해도 옛 화면(웹 플레이어·tvOS)은 응답을 형식 검사 없이 받아
 * `none` 이 아니면 페이드로 넘어간다 — 서버가 먼저 바뀌어도 화면이 깨지지 않는다.
 * 움직임은 `transform`·`opacity` 만 쓴다. 안드로이드 5.1 웹뷰와 저사양 셋톱에서도 GPU 가 바로 합성한다.
 */
export const TRANSITIONS = ['fade', 'slide', 'slide-up', 'zoom', 'cover', 'flip', 'tiles', 'none'];
//# sourceMappingURL=types.js.map